import { FormEvent, useMemo, useRef } from 'react';
import { KeyedMutator } from 'swr';
import { useClient } from '../../../hooks/useClient';
import { useForm } from '../../../hooks/useForm';
import { Organization } from '../../../types/organization';
import { getInitialOrgEmailSettingsForm, ORG_EMAIL_SETTINGS_FORM_VALIDATORS, OrgEmailSettingsForm } from '../validators';

interface Options {
  organization: Organization | undefined;
  mutateOrganization: KeyedMutator<Organization | undefined>;
}

export const useOrgEmailSettingsForm = ({ organization, mutateOrganization }: Options) => {
  const { patch } = useClient();

  const orgEmailSettingsFormRef = useRef(null);
  const initialOrgEmailSettingsForm = useMemo(() => getInitialOrgEmailSettingsForm(organization), [organization]);
  const {
    form: orgEmailSettings,
    setFormFields: setOrgEmailSettingsField,
    isSaving: orgEmailSettingsSaving,
    setIsSaving: setOrgEmailSettingsSaving,
    hasChanged: orgEmailSettingsChanged,
    validationErrors: orgEmailSettingsErrors,
    validate: validateOrgEmailSettings,
  } = useForm<OrgEmailSettingsForm | undefined>(
    initialOrgEmailSettingsForm,
    false,
    ORG_EMAIL_SETTINGS_FORM_VALIDATORS,
    orgEmailSettingsFormRef,
  );

  const handleOrgEmailLocalPartChange = (localPart: string) => {
    setOrgEmailSettingsField({ emailLocalPart: localPart });
  };

  const handleOrgEmailSettingsSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errors = validateOrgEmailSettings();
    if (errors || !orgEmailSettings || !orgEmailSettings.emailLocalPart || !orgEmailSettings.emailDomain || !organization) return;

    setOrgEmailSettingsSaving(true);

    const fullEmail = orgEmailSettings.emailLocalPart + orgEmailSettings.emailDomain;

    const updatedOrg = await patch<Organization>(`/organizations/${organization.id}`, {
      body: { email: fullEmail },
      errorMessages: {
        summary: 'Error al configurar el correo electrónico',
        defaultDetail: 'No se ha podido modificar el correo electrónico de la empresa.',
      },
      successMessage: {
        summary: 'Configuración de correo electrónico modificada',
        detail: 'El correo electrónico de la empresa se ha modificado correctamente.',
      },
    });

    if (updatedOrg) {
      mutateOrganization?.();
    }
    setOrgEmailSettingsSaving(false);
  };

  return {
    formRef: orgEmailSettingsFormRef,
    form: orgEmailSettings,
    isSaving: orgEmailSettingsSaving,
    hasChanged: orgEmailSettingsChanged,
    validationErrors: orgEmailSettingsErrors,
    handleLocalPartChange: handleOrgEmailLocalPartChange,
    handleSubmit: handleOrgEmailSettingsSubmit,
  };
};

export type OrgEmailSettingsFormState = ReturnType<typeof useOrgEmailSettingsForm>;
